import {
  ChangelogFilters,
  ClaimFilters,
  ExclusionFilters,
  PolicyFilters,
} from '#imports'
import { FilterNames } from '@rialtic/types'

declare module 'vue-router' {
  interface RouteMeta {
    defaultQuery?: Record<string, string | string[]>
  }
}

const getRouteSection = (name: unknown) =>
  typeof name === 'string' ? name.split('-')[0] : ''

const PageFilters: Record<string, string[]> = {
  changelog: ChangelogFilters,
  claims: ClaimFilters,
  exclusions: ExclusionFilters,
  policies: PolicyFilters,
}

export default defineNuxtRouteMiddleware((to, from) => {
  const { activeUrlParams, clearUrlParams, deleteUrlParam, setUrlParams } =
    useUrlParamsState()

  const fromSection = getRouteSection(from?.name)
  const toSection = getRouteSection(to.name)
  const isTopLevelNavigation =
    toSection && fromSection && toSection !== fromSection

  // Clear conflicting params when switching between sections
  if (isTopLevelNavigation) {
    const toFilters = PageFilters[toSection]
    const fromFilters = PageFilters[fromSection]

    // Clear sort when navigating between top level pages
    if (activeUrlParams[FilterNames.SORT_BY]) {
      deleteUrlParam(FilterNames.SORT_BY)
    }

    if (toFilters && fromFilters) {
      for (const key of Object.keys(activeUrlParams)) {
        if (fromFilters.includes(key) && toFilters.includes(key)) {
          deleteUrlParam(key)
        }
      }
    }
  }

  // Clear all params except those relevant to the requested page
  switch (to.name) {
    case 'claims':
      clearUrlParams(ClaimFilters)
      break
    case 'claims-id':
      clearUrlParams([...ClaimFilters, ...ClaimDetailsFilters])
      break
    case 'claims-id-insightId-influencing-claims':
      clearUrlParams(InfluencingClaimsFilters)
    case 'changelog':
      clearUrlParams(ChangelogFilters)
      break
    case 'exclusions':
    case 'exclusions-create':
    case 'exclusions-id':
    case 'exclusions-id-edit':
      clearUrlParams(ExclusionFilters)
      break
    case 'policies':
    case 'policies-id':
    case 'policies-id-edit-modes':
    case 'dashboard-policies':
    case 'dashboard-policies-id':
      clearUrlParams(PolicyFilters)
      break
    case 'policies-id-providers-impacted':
    default:
      clearUrlParams()
      break
  }

  // Set default params defined by definePageMeta
  if (to.meta.defaultQuery) {
    for (const key in to.meta.defaultQuery) {
      if (!activeUrlParams[key] && isTopLevelNavigation) {
        setUrlParams(key, to.meta.defaultQuery[key])
      }
    }
  }

  // handles NuxtLink / RouterLink string urls with query params
  for (const key in to.query) {
    setUrlParams(key, to.query[key] as string | string[])
  }
})
